import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { Invoice } from '../invoice';
import { InvoiceService } from '../invoice.service';
import { AlertService } from '../alert.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Location, NgIf, NgSwitch, NgSwitchCase, NgFor, CurrencyPipe } from '@angular/common';
import { ProposalService } from '../proposal.service';
import { PaymentService } from '../payment.service';
import { Payment } from '../payment';
import { SendEmailComponent } from '../send-email/send-email.component';
import { NgbModal, NgbCalendar, NgbDateParserFormatter, NgbTooltip, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, NgbInputDatepicker, NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { faAngleLeft, faTrashCan, faEnvelope, faEye, faCalendar, faCheck, faTimes, faSave, faExternalLinkAlt, faPlus, faQuestionCircle, faPencil, faSms, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { AcceptedProposal } from '../accepted-proposal';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { PaymentModalComponent } from '../payment-modal/payment-modal.component';
import { SendSmsDialogComponent } from '../conversation/components/send-sms-dialog/send-sms-dialog.component';
import { ConversationService } from '../conversation.service';
import { Subject, takeUntil } from 'rxjs';
import { User } from '../user';
import { PaymentCategoryPipe } from '../payment-category.pipe';
import { FormatDateTimePipe } from '../format-date-time.pipe';
import { NotesComponent } from '../notes/notes.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { CreditNoteListComponent } from "../shared/components/credit-note-list/credit-note-list.component";

@Component({
    selector: 'app-invoice-detail',
    templateUrl: './invoice-detail.component.html',
    styleUrls: ['./invoice-detail.component.css'],
    imports: [NgbTooltip, FaIconComponent, NgIf, RouterLink, NgbDropdown, NgbDropdownToggle, NgbDropdownMenu, NgbDropdownButtonItem, NgbDropdownItem, ReactiveFormsModule, NgbInputDatepicker, NgbPopover, NgSwitch, NgSwitchCase, NgFor, NotesComponent, CurrencyPipe, FormatDateTimePipe, PaymentCategoryPipe, CreditNoteListComponent]
})
export class InvoiceDetailComponent implements OnInit {

  // Forms
  invoiceForm: UntypedFormGroup;
  sendInvoiceForm: UntypedFormGroup;

  // Invoice
  invoiceId: string;
  invoice: Invoice;

  // Accepted Proposal
  acceptedProposal: AcceptedProposal;

  // Payments
  payments: Payment[] = [];
  totalDebitAmount: number = 0;
  totalCreditAmount: number = 0;
  isPaid: boolean = false;
  remainingBalance: number;

  // Credit Notes
  refreshCreditNotes: boolean = false;

  // Font Awesome Properties
  faAngleLeft = faAngleLeft;
  faTrashCan = faTrashCan;
  faEnvelope = faEnvelope;
  faEye = faEye;
  faCalendar = faCalendar;
  faCheck = faCheck;
  faTimes = faTimes;
  faSave = faSave;
  faExternalLinkAlt = faExternalLinkAlt;
  faPlus = faPlus;
  faQuestionCircle = faQuestionCircle;
  faPencil = faPencil;
  faSms = faSms;
  faPaperPlane =  faPaperPlane; 
  invoices: Invoice[] = [];
  twilioClient: any;
  componentDestroyed$: Subject<boolean> = new Subject();
  loginDetails: User;

  constructor(private invoiceService: InvoiceService,
    private paymentService: PaymentService,
    private proposalService: ProposalService,
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertService,
    private modalService: NgbModal,
    private calendar: NgbCalendar,
    private dateParserFormatter: NgbDateParserFormatter,
    public location: Location,
    private conversationService: ConversationService) { }

  ngOnInit(): void {
    this.loginDetails = JSON.parse(localStorage.getItem('login'));
    this.invoiceId = this.route.snapshot.paramMap.get('invoiceId');
    this.invoiceForm = new UntypedFormGroup({
      balanceType: new UntypedFormControl('PERCENT'),
      balance: new UntypedFormControl(50),
      issueDate: new UntypedFormControl(this.calendar.getToday()),
      dueDate: new UntypedFormControl(this.calendar.getNext(this.calendar.getToday(), 'd', 30)),
      status: new UntypedFormControl('PENDING')
    });
    this.getInvoice();
    this.listenForBalanceTypeChanges();
    this.listenForIssueDateChanges();

    this.conversationService.twilioClient
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((data) => {
        this.twilioClient = data;
      });

  }

  unlockInvoiceForm() {
    this.invoiceForm.enable();
  }

  ngOnDestroy(): void {
    this.modalService.hasOpenModals() && this.modalService.dismissAll();
  }

  // Open Delete Invoice Confirmation Modal
  openDeleteInvoiceConfirmationModal(): void {
    const confirmationModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmationModalRef.componentInstance.message = "Are you sure you would like to delete this invoice?";
    confirmationModalRef.componentInstance.actionBtnTitle = "Delete";
    confirmationModalRef.componentInstance.confirmed.subscribe(() => {
      this.deleteInvoice();
    });
  }

  // Open Send Email Modal For Invoice
  openSendEmailModalForInvoice(): void {
    const modalRef = this.modalService.open(SendEmailComponent);
    modalRef.componentInstance.clientId = this.invoice.clientId;
    modalRef.componentInstance.invoiceId = this.invoice.id;
    modalRef.componentInstance.type = 'INVOICE';
    modalRef.componentInstance.subject = `Invoice #${this.invoice.number}`;
    modalRef.componentInstance.emailSent.subscribe(() => {
      this.getInvoice();
    });
  }

  // Open Payment Modal
  openPaymentModal(payment: Payment = null): void {
    this.refreshCreditNotes = false;
    const modalRef = this.modalService.open(PaymentModalComponent);
    modalRef.componentInstance.clientId = this.invoice.clientId;
    modalRef.componentInstance.invoiceId = this.invoice.id;
    modalRef.componentInstance.invoiceNumber = this.invoice.number;
    modalRef.componentInstance.invoiceTotal = this.invoice.amount;
    modalRef.componentInstance.projectId = this.invoice.projectId
    modalRef.componentInstance.totalDebitAmount = this.totalDebitAmount;
    if (payment) modalRef.componentInstance.payment = payment;
    modalRef.componentInstance.saved.subscribe(() => {
      this.getInvoice();
    this.refreshCreditNotes = true;
    });
  }

  // Listen For Balance Type Changes
  private listenForBalanceTypeChanges(): void {
   this.invoiceForm.controls.balanceType.valueChanges.subscribe((value) => {
      if (value === 'DOLLAR') {
        const dollarBalance =
          this.remainingBalance ?? this.invoice.amount;

        this.invoiceForm.controls.balance.setValue(
          Math.round((Number(dollarBalance) + Number.EPSILON) * 100) / 100
        );
      } else {
        if (this.acceptedProposal?.total) {
          const dollarBalance =
            this.remainingBalance ?? this.invoice.amount;

          const percentage =
            (Number(dollarBalance) / Number(this.acceptedProposal.total)) * 100;

          this.invoiceForm.controls.balance.setValue(
            Math.round(percentage)
          );
        } else {
          this.invoiceForm.controls.balance.setValue(
            this.invoice.balance ?? 50
          );
        }
      }
    });
  }

  // Listen For Issue Date Changes
  private listenForIssueDateChanges(): void {
    this.invoiceForm.controls.issueDate.valueChanges.subscribe((issueDate) => {
      if (issueDate !== null) this.invoiceForm.controls.dueDate.setValue(this.calendar.getNext(issueDate, 'd', 7));
    });
  }

  // Get Invoice
  private getInvoice(): void {
    this.invoiceService.getInvoice(this.invoiceId).subscribe((invoice) => {
      this.invoice = invoice;
      this.getAcceptedProposal();
      this.getPayments();
      this.getInvoicesForProject(invoice.projectId);
      this.setInvoiceFormValues();
    });
  }

  // Get Invoices For Project
  private getInvoicesForProject(id: any): void {
    const params = {
      searchTerm: null,
      sortBy: 'i.invoice_number',
      sortDirection: 'DESC',
      limit: null,
      offset: null,
      'filter:proj.project_id': JSON.stringify([id])
    };
    this.invoiceService.getInvoices(params).subscribe((res) => {
      this.invoices = res.invoices;
      this.generateBalance()
    });
  };

  // Get Payments
  private getPayments(): void {
    let params = {
      searchTerm: null,
      sortBy: 'pay.payment_date',
      sortDirection: 'ASC',
      limit: null,
      offset: null,
      'filter:pay.invoice_id': JSON.stringify([this.invoiceId])
    };
    this.paymentService.getPayments(params).subscribe((res) => {
      this.payments = res.payments;
      const withoutRejects = res.payments.filter(payment => payment.status != 'REJECTED' && payment.status != 'RETURNED');
      this.totalDebitAmount = withoutRejects.reduce((acc, payment) => { return acc + ((payment.type == 'DEBIT' || payment.type == 'SHORT_PAYMENT') ? payment.amount : 0); }, 0);
      this.totalCreditAmount = withoutRejects.reduce((acc, payment) => { return acc + ((payment.type == 'CREDIT') ? payment.amount : 0); }, 0);
    });
  };

  // Get Accepted Proposal
  private getAcceptedProposal(): void {
    this.proposalService.getAcceptedProposalForProject(this.invoice.projectId).subscribe((acceptedProposal) => {
      this.acceptedProposal = acceptedProposal;
    });
  }

  // Set Invoice Form Values
  private setInvoiceFormValues(): void {
    this.invoiceForm.controls.balanceType.setValue(this.invoice.balanceType);
    this.invoiceForm.controls.balance.setValue(this.invoice.balance);
    this.invoiceForm.controls.issueDate.setValue(this.dateParserFormatter.parse(this.invoice.issueDate));
    this.invoiceForm.controls.dueDate.setValue(this.dateParserFormatter.parse(this.invoice.dueDate));
    this.invoiceForm.controls.status.setValue(this.invoice.status);

    if (this.invoice.status == 'PAID') {
      this.invoiceForm.disable();
      this.isPaid = true;
    }
  }

  // Update Invoice
  updateInvoice(): void {
    let message: string = '';
    let newAmount: number = 0;
    if (this.invoiceForm.valid) {
      const amountFromInputField = this.invoiceForm.value.balance;
      const balanceTypeDollarNotChange = this.invoice.balanceType === 'DOLLAR' && this.invoiceForm.value.balanceType === 'DOLLAR';
      const balanceTypePercentNotChange = this.invoice.balanceType === 'PERCENT' && this.invoiceForm.value.balanceType === 'PERCENT';

      // Dollar no changes in balance type and invoice amount.
      if ((balanceTypeDollarNotChange || balanceTypePercentNotChange) && this.invoice.balance === amountFromInputField) {
        this.saveUpdatedInvoice();
      }

      // Dollar no changes in balance type and has changes in invoice amount.
      if (balanceTypeDollarNotChange && this.invoice.balance !== amountFromInputField) {
        message = `The <b>Invoice Amount</b> has changed from <b>$${this.invoice.balance.toFixed(2)}</b> to <b>$${amountFromInputField.toFixed(2)}</b>. Please confirm this change before you continue.`;
        this.openConfirmationModal(message);
      }

      // Dollar change to Percent.
      if (this.invoice.balanceType === 'DOLLAR' && this.invoiceForm.value.balanceType !== 'DOLLAR') {
        newAmount = this.acceptedProposal.total * (amountFromInputField / 100);
        message = `The <b>Balance Type</b> has changed from <b>Dollar</b> to <b>Percent</b>. The new invoice total is <b>$${newAmount.toFixed(2)}</b>, which is <b>${amountFromInputField}%</b> of the Accepted Proposal. Please confirm this change before you continue.`;
        this.openConfirmationModal(message);
      }

      // Percent change to Dollar.
      if (this.invoice.balanceType === 'PERCENT' && this.invoiceForm.value.balanceType !== 'PERCENT') {
        message = `The <b>Balance Type</b> has changed from <b>Percent</b> to <b>Dollar</b>. The new invoice total is <b>$${amountFromInputField.toFixed(2)}</b>. Please confirm this change before you continue.`;
        this.openConfirmationModal(message);
      }

      // Percent not change and invoice amount field has change.
      if (balanceTypePercentNotChange && this.invoice.balance !== amountFromInputField) {
        newAmount = this.acceptedProposal.total * (amountFromInputField / 100);
        message = `The <b>Invoice Amount</b> has changed from <b>${this.invoice.balance}%</b> to <b>${amountFromInputField}%</b>, with a new invoice total of <b>$${newAmount.toFixed(2)}</b>. Please confirm this change before you continue.`;
        this.openConfirmationModal(message);
      }
    } else {
      this.invoiceForm.markAllAsTouched();
    }
  }

  // Open Confirmation Modal
  openConfirmationModal(message: string) {
    const modalRef = this.modalService.open(ConfirmationModalComponent);
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.actionBtnTitle = "Continue";
    modalRef.componentInstance.confirmed.subscribe(() => {
      this.saveUpdatedInvoice();
    });
  }

  // Save Updated Invoice
  saveUpdatedInvoice() {
    const invoice = {
      id: this.invoiceId,
      projectId: this.invoice.projectId,
      balanceType: this.invoiceForm.value.balanceType,
      balance: this.invoiceForm.value.balance,
      issueDate: this.dateParserFormatter.format(this.invoiceForm.value.issueDate),
      dueDate: this.dateParserFormatter.format(this.invoiceForm.value.dueDate),
      status: this.invoiceForm.value.status
    };
    this.invoiceService.updateInvoice(invoice).subscribe(() => {
      this.alertService.showSuccessAlert('Invoice Updated');
      this.getInvoice();
    });
  }

  // Delete Invoice
  deleteInvoice(): void {
    this.invoiceService.deleteInvoice(this.invoiceId).subscribe(() => {
      this.location.back();
    });
  }

  // General Balance
  generateBalance(isClick: boolean = false) {
    if (isClick) {
      this.invoiceForm.controls.balanceType.setValue('DOLLAR');

      const balanceType = this.invoiceForm.controls.balanceType.value;
      const paidAndPendingInvoices = this.invoices.filter(invoice => (invoice.status != 'CANCELED'));

      const totalInvoicesBalance = this.invoices.length == 0 ? 0 : this.summ(paidAndPendingInvoices, 'amount');
      // const balancePercentage = (totalInvoicesBalance / this.acceptedProposal.total) * 100;

      let remainingBalanceFromOtherInvoice = this.acceptedProposal.total - totalInvoicesBalance;
      this.remainingBalance = this.invoice.amountDue + remainingBalanceFromOtherInvoice;

      if (balanceType == 'PERCENT') {
        this.invoiceForm.controls.balance.setValue(((this.remainingBalance * 100) / this.acceptedProposal.total).toFixed(2))
      } else if (balanceType == 'DOLLAR') {
        if (Math.round((this.remainingBalance + Number.EPSILON) * 100) / 100 === this.invoice.amount) {
          this.invoiceForm.controls.balance.setValue(this.invoice.amount);
        } else {
          this.invoiceForm.controls.balance.setValue(Math.round((this.remainingBalance + Number.EPSILON) * 100) / 100);// for rounding 2 decimal places
        }
      }
    }
  }

  summ = (arr: any[], property: string) => arr.reduce(function (acc, obj) { return acc + obj[property]; }, 0);

  // Invoice Form Accessors
  get balance() { return this.invoiceForm.controls.balance; }
  get issueDate() { return this.invoiceForm.controls.issueDate; }
  get dueDate() { return this.invoiceForm.controls.dueDate; }

  // View Invoice
  viewInvoice(): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/invoices/${this.invoiceId}/view`]));
    window.open(url);
  }

  // Open Send SMS Modal
  openSendSmsModal(): void {
    const modalRef = this.modalService.open(SendSmsDialogComponent);
    modalRef.componentInstance.clientId = this.invoice.clientId;
    modalRef.componentInstance.proposalId = this.invoice.id;
    modalRef.componentInstance.contact = this.invoice.contacts;
    modalRef.componentInstance.clientName = this.invoice.clientName;
    modalRef.componentInstance.twilioClient = this.twilioClient;
    modalRef.componentInstance.defaultMessage = 'You have a new invoice from <OrgName>. Click here to pay: <LINK>';
    modalRef.componentInstance.sent.subscribe((res) => {
      this.alertService.showSuccessAlert('Message sent');
    });
  }

  applyCreditNoteSuccess(isSuccess: boolean) {
    if(isSuccess) {
      this.getPayments();
      this.getInvoice();
      this.listenForBalanceTypeChanges();
      this.listenForIssueDateChanges();
    }
  }

}
